/* eslint-disable react/prop-types */
import React from 'react';
import { resolveMediaPath } from '@/shared/libs/resolve-media-path';
import { getWebpPngFallbackSrc } from '@/shared/libs/get-webp-png-fallback-src';
import { Box } from '@/shared/ui/box/Box';
import { getThemeBrand } from '@/shared/hooks/useBrandTheme';

interface ImageProps {
  src: string;
  alt?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  style?: React.CSSProperties;
  className?: string;
  width?: string | number;
  height?: string | number;
  styleForBox?: React.CSSProperties;
  fallback?: React.ReactNode;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'eager' | 'lazy';
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  mobileSrc?: string;
  mobileBreakpoint?: number;
}

const DefaultFallbackImage = ({
  width,
  height,
  style,
  onLoad,
}: {
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
}) => {
  const loader = getThemeBrand().assets.loader;

  return (
    <img
      src={window.innerHeight <= 768 ? loader?.mobile : loader?.default}
      alt="default-image"
      width={width}
      height={height}
      style={{
        objectFit: 'cover',
        ...style,
      }}
      loading="lazy"
      onLoad={onLoad}
    />
  );
};

const Image = React.memo(
  React.forwardRef<HTMLDivElement, ImageProps>(
    (
      {
        src,
        alt,
        onClick,
        style,
        className,
        width,
        height,
        styleForBox,
        fetchPriority = 'auto',
        loading = 'lazy',
        onLoad,
        mobileSrc,
        mobileBreakpoint = 768,
        fallback = (
          <DefaultFallbackImage
            width={width}
            height={height}
            style={style}
            onLoad={onLoad}
          />
        ),
        ...props
      },
      ref
    ) => {
      const [hasError, setHasError] = React.useState(false);

      React.useEffect(() => {
        setHasError(false);
      }, [src, mobileSrc]);

      const image = (
        <img
          src={resolveMediaPath(src)}
          alt={alt}
          width={width}
          height={height}
          style={style}
          loading={loading}
          fetchPriority={fetchPriority}
          onLoad={onLoad}
          onError={() => setHasError(true)}
        />
      );

      return (
        <Box
          ref={ref}
          className={className}
          onClick={onClick}
          {...props}
          style={styleForBox}
        >
          {hasError || !src ? (
            fallback
          ) : mobileSrc ? (
            <picture>
              <source
                media={`(max-width: ${mobileBreakpoint}px)`}
                srcSet={resolveMediaPath(mobileSrc)}
              />
              {image}
            </picture>
          ) : (
            image
          )}
        </Box>
      );
    }
  )
);

const webpPngFallback = (
  src: string | null | undefined,
  alt: string,
  extra?: {
    style?: React.CSSProperties;
    width?: string | number;
    height?: string | number;
  }
) => {
  const pngSrc = getWebpPngFallbackSrc(src);
  return pngSrc ? <img src={pngSrc} alt={alt} {...extra} /> : undefined;
};

export { Image, webpPngFallback };
